import { cn } from "@onclicks/sharedcomponents/ui";
import React, { forwardRef, InputHTMLAttributes, ReactNode, useId } from "react";
import Icon from "../shared/ui/icon";

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  titre: string;
  icon?: string;
  error?: string;
  description?: ReactNode;
  containerClassName?: string;
  unit?: string;
}

export const Field = forwardRef<HTMLInputElement, FieldProps>(
  (
    {
      titre,
      icon,
      error,
      description,
      containerClassName,
      className,
      type = "text",
      ...props
    },
    ref,
  ) => {
    const generatedId = useId();
    const id = props.id || (props.name ? `field-${props.name}` : generatedId);
    const isRange = type === "range";
    const isCheckbox = type === "checkbox";
    const isDateTime = type === "datetime-local";

    if (isCheckbox) {
      return (
        <div className={cn("group", containerClassName)}>
          <label
            htmlFor={id}
            className="flex items-center gap-3 p-3 rounded-lg cursor-pointer
                           bg-slate-50/50 dark:bg-slate-50/10
                           border border-slate-200 dark:border-slate-300
                           has-checked:border-white has-checked:bg-slate-50/10
                           hover:border-slate-300 dark:hover:border-primary
                           transition-all duration-200"
          >
            <div className="relative flex items-center justify-center w-5 h-5 shrink-0">
              <input
                {...props}
                ref={ref}
                id={id}
                type="checkbox"
                className={cn(
                  "peer w-5 h-5 rounded appearance-none cursor-pointer",
                  "border-2 border-slate-300 dark:border-slate-300",
                  "checked:bg-primary checked:border-primary",
                  "focus:outline-none focus:ring-2 focus:ring-primary/30",
                  "transition-all duration-200",
                  className,
                )}
              />
              <Icon
                name="check"
                className="absolute text-white text-sm
                           opacity-0 peer-checked:opacity-100 pointer-events-none
                           transition-opacity duration-200"
              />
            </div>

            <div className="flex-1 min-w-0">
              <span className="text-sm font-semibold text-slate-700 dark:text-white">
                {titre}
              </span>
              {description && (
                <div className="text-xs text-slate-400 mt-0.5">{description}</div>
              )}
            </div>
          </label>
        </div>
      );
    }

    return (
      <div className={cn("space-y-2", containerClassName)}>
        <label
          htmlFor={id}
          className="text-sm font-semibold text-slate-600 dark:text-white"
        >
          {titre}
        </label>

        <div className={cn("relative", isRange && "flex items-center gap-4")}>
          {icon && !isRange && (
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Icon name={icon} className="text-lg text-slate-400" />
            </div>
          )}

          <input
            {...props}
            ref={ref}
            id={id}
            type={type}
            aria-invalid={!!error}
            className={cn(
              "transition-all duration-200 focus:outline-none",

              "text-slate-900 placeholder-slate-300",

              // Style pour tout autres types que range
              !isRange &&
                "w-full bg-slate-50 rounded-lg p-3 border border-slate-200 dark:border-slate-300 focus:ring-2 focus:ring-primary ",

              // Style spécifique au Range
              isRange &&
                "flex-1 accent-primary h-2 rounded-lg cursor-pointer pb-3",

              // Gestion des erreurs et icônes
              error && "ring-2 ring-red-500",
              icon && !isRange && "pl-10",
              className,
            )}
          />

          {/* Affichage de la valeur si c'est un Range */}
          {isRange && (
            <span className="bg-primary/20 text-primary font-bold px-3 py-1 rounded-lg min-w-[8ch] text-center ">
              {props.value} {props.unit}
            </span>
          )}
        </div>

        {/* Description */}
        {description && !error && (
          <p className="text-xs text-slate-500 italic">{description}</p>
        )}

        {error && <p className="text-xs text-red-600 font-medium">{error}</p>}
      </div>
    );
  },
);

Field.displayName = "Field";
